import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const FALLBACK = {
  business_name: "P & A Cleaning",
  business_secondary_name: "P & A Cleaning / Paula's Cleaning Service",
  phone: "916-224-7260",
  phone_href: "tel:9162247260",
  email: "Info@pacleans.com",
  service_area: "Sacramento and surrounding areas",
  license_number: "Lic#SPB12022-00319",
  website: "pacleans.com",
  instagram: "@pacleaning_",
  instagram_url: "https://instagram.com/pacleaning_",
  facebook: "@paulacleans",
  facebook_url: "https://facebook.com/paulacleans",
  yelp_url: "https://m.yelp.com/biz/paulas-cleaning-service-arden-arcade",
  homepage_headline: "Move-In, Move-Out & Commercial Cleaning in Sacramento",
  homepage_subheadline:
    "Reliable per-job cleaning estimates for move-outs, pre-listings, recurring home cleaning, office cleaning, junk removal, carpet cleaning, and yard cleanup.",
  main_cta_text: "Request an Estimate",
  secondary_cta_text: "Call / Text Now",
  pricing_disclaimer:
    "Prices shown are ballpark estimates only. Final pricing is per job, not per hour, and may change after inspection or review of photos.",
  footer_text: "© P & A Cleaning. All rights reserved.",
  business_hours: "",
};

const SettingsContext = createContext(FALLBACK);

function deriveTelHref(phone) {
  if (!phone) return FALLBACK.phone_href;
  const digits = String(phone).replace(/[^\d]/g, "");
  return digits ? `tel:${digits}` : FALLBACK.phone_href;
}

function injectLocalBusinessSchema(s) {
  try {
    const id = "ld-localbusiness";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("script");
      tag.id = id;
      tag.type = "application/ld+json";
      document.head.appendChild(tag);
    }
    const sameAs = [s.instagram_url, s.facebook_url, s.yelp_url].filter(Boolean);
    const data = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: s.business_name,
      alternateName: s.business_secondary_name || undefined,
      url: s.website ? `https://${s.website.replace(/^https?:\/\//, "")}` : undefined,
      telephone: s.phone,
      email: s.email,
      areaServed: s.service_area,
      identifier: s.license_number || undefined,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Sacramento",
        addressRegion: "CA",
        addressCountry: "US",
      },
      sameAs,
    };
    Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
    tag.textContent = JSON.stringify(data);
  } catch (_) {
    // best-effort, never throw from schema injection
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);

  useEffect(() => {
    api
      .get("/public/settings")
      .then((r) => {
        const fetched = r?.data || {};
        const merged = { ...FALLBACK, ...fetched };
        // Always derive phone_href from the live phone value so admin changes propagate.
        merged.phone_href = deriveTelHref(merged.phone);
        setSettings(merged);
        injectLocalBusinessSchema(merged);
      })
      .catch(() => {
        setSettings(FALLBACK);
        injectLocalBusinessSchema(FALLBACK);
      });
  }, []);

  return (
    <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>
  );
}

export function usePublicSettings() {
  return useContext(SettingsContext);
}

/**
 * Returns a BUSINESS-shaped object derived from the live public settings.
 * Drop-in replacement for the static `BUSINESS` constant in `@/data/content`,
 * so pages can swap `import { BUSINESS } from "@/data/content"` for
 * `const BUSINESS = useBusiness()` with zero further changes.
 */
export function useBusiness() {
  const s = useContext(SettingsContext);
  return useMemo(
    () => ({
      name: s.business_name,
      full: s.business_secondary_name,
      short: s.business_name,
      area: s.service_area,
      phone: s.phone,
      phoneHref: s.phone_href,
      email: s.email,
      emailHref: s.email ? `mailto:${s.email}` : "",
      website: s.website,
      instagram: s.instagram,
      instagramUrl: s.instagram_url,
      facebook: s.facebook,
      facebookUrl: s.facebook_url,
      yelpUrl: s.yelp_url,
    }),
    [s],
  );
}
